<template>
  <v-row justify="center">
    <v-dialog
      v-model="open"
      width="600px"
      :fullscreen="isMobileBreakpoint"
      :persistent="isMobileBreakpoint"
    >
      <v-card>
        <v-toolbar
          elevation="0"
          color="background"
          class="pt-4 pb-4"
          height="94px"
        >
          <v-toolbar-title class="toolbar-title ml-2">
            {{ $t('message.pages.organization.directCancel.title') }}
            <p class="text-caption">
              {{ $t('message.pages.organization.directCancel.subtitle') }}
            </p>
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn icon @click="handleClose()">
              <v-icon
                size="24"
                color="grey"
              >
                mdi-close
              </v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
        <v-card-text
          class="pl-6 pr-6 pt-0 pb-6 renameVideoModalDescription"
          :style="isMobileBreakpoint ? { height: 'calc(100vh - 90px - 91px)', overflowY: 'auto' } : {}"
        >
          <v-container style="margin-top: 2px; padding: 0;">
            <v-row no-gutters>
              <v-col cols="12" class="instructions">
                <p class="body-regular-small mb-2">
                  {{ $t('message.pages.organization.directCancel.description') }}
                </p>
              </v-col>
              <v-col cols="12" class="mt-0" style="margin-bottom: 19px;">
                <BaseAlert
                  type="danger"
                  :custom-svg="require('@/assets/snackbar/icons/alert-solid-red.svg')"
                  description=""
                  :title="$t('message.pages.organization.directCancel.alertTitle')"
                  :html-description="alertDescription"
                />
              </v-col>
              <v-col
                cols="12"
                class="container-title-description description-input pa-0 mt-6"
                style="margin-bottom: 19px;"
              >
                <base-textarea
                  v-model="cancelData.reason"
                  :placeholder="$t('message.pages.organization.directCancel.inputDescription.placeholder')"
                  :label="$t('message.pages.organization.directCancel.inputDescription.label')"
                  outlined
                  dense
                  autofocus
                  color="primary"
                  :counter="maxLength"
                  auto-grow
                  @focus="inputBeingEdited('reason')"
                  @blur="cancelEdit"
                  :class="{ inputActive: activeInput === 'reason' }"
                  :hide-details="shouldHideDetails"
                  :rules="[maxLengthRule, requiredFieldRule]"
                  ref="reason"
                  @keyup.escape="cancelEdit"
                />
              </v-col>
              <v-col
                cols="12"
                class="mt-6 password-input"
              >
                <v-input
                  v-model="cancelData.password"
                  :placeholder="$t('message.pages.organization.directCancel.inputPassword.placeholder')"
                  :label="$t('message.pages.organization.directCancel.inputPassword.label')"
                  autocomplete="off"
                  :error="errors.passwordError"
                  :messages="passwordErrorMessages"
                  :append-icon="showPass ? 'mdi-eye' : 'mdi-eye-off'"
                  @click:append="showPass = !showPass"
                  :type="showPass ? 'text' : 'password'"
                  @focus="errors.passwordError = false"
                />
              </v-col>
              <v-col
                cols="12"
              >
                <v-checkbox
                  v-model="cancelData.confirmAction"
                  hide-details
                >
                  <template v-slot:label>
                    <span style="font-size: 14px">{{
                      $t('message.pages.organization.directCancel.inputConfirmation')
                    }}</span>
                  </template>
                </v-checkbox>
              </v-col>
              <v-col cols="12" v-if="apiError">
                <span class="api-error-message">{{ apiError }}</span>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions
          class="px-8 py-6"
          :class="isMobileBreakpoint ? 'mobile-actions' : ''"
        >
          <v-spacer></v-spacer>
          <base-btn
            type="tertiary"
            text
            class="mr-4 px-4"
            :disabled="isFetching"
            @click="handleClose"
          >
            {{ $t('message.pages.organization.directCancel.continueUsing') }}
          </base-btn>

          <v-tooltip bottom :disabled="canCancel">
            <template v-slot:activator="{ on, attrs }">
              <div class="button-container" v-bind="attrs" v-on="on">
                <base-btn
                  type="danger"
                  large
                  min-width="185px"
                  @click="handleCancellation"
                  :loading="isFetching"
                  :disabled="!canCancel"
                >
                  {{ $t('message.pages.organization.directCancel.cancelAccount') }}
                </base-btn>
              </div>
            </template>
            <span style="white-space: pre-line;">{{ $t('message.pages.organization.directCancel.cancelAccountTooltip') }}</span>
          </v-tooltip>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex'
import BaseAlert from '@/components/BaseAlert.vue'
import BaseTextarea from '@/components/BaseComponents/BaseTextarea.vue'
import i18n from '@/translations/lang'
import planApi from '@/services/planApi'

export default {
  name: 'CancelSubscriptionDirectModal',
  components: {
    BaseAlert,
    BaseTextarea
  },
  data: () => ({
    open: false,
    isFetching: false,
    activeInput: null,
    maxLength: 5000,
    showPass: false,
    errors: {
      exceededMaxLength: false,
      passwordError: false,
    },
    cancelData: {
      reason: '',
      password: '',
      confirmAction: false
    },
    unsubscribeToken: null,
    apiError: null,
  }),
  computed: {
    ...mapState('user', {
      isMobileBreakpoint: 'isMobileBreakpoint',
    }),
    ...mapState({
      userPlanInfo: (state) => state.user.userPlanInfo,
    }),
    currentLang() {
      return this.$i18next.resolvedLanguage || 'pt'
    },
    canCancel() {
      const { confirmAction, password, reason } = this.cancelData

      return Boolean(confirmAction) &&
        password.trim() !== '' &&
        reason.trim() !== '' &&
        reason.length <= this.maxLength
    },
    shouldHideDetails () {
      return this.activeInput !== 'reason' && !this.errors.exceededMaxLength
    },
    alertDescription() {
      const items = {
        item1: this.$t('message.pages.organization.directCancel.alertDescription.item_1'),
        item2: this.$t('message.pages.organization.directCancel.alertDescription.item_2'),
        item3: this.$t('message.pages.organization.directCancel.alertDescription.item_3')
      }

      return `
        <ul>
          <li>${items.item1}</li>
          <li>${items.item2}</li>
          <li>${items.item3}</li>
        </ul>
      `
    },
    passwordErrorMessages() {
      return this.errors.passwordError
        ? [this.$t('message.pages.organization.directCancel.inputPassword.invalid')]
        : []
    },
    maxLengthRule () {
      return (v) => {
        if (!v || v.length <= this.maxLength) {
          this.errors.exceededMaxLength = false
          return true
        } else {
          this.errors.exceededMaxLength = true
          return this.$t('message.pages.organization.directCancel.inputDescription.maxLength', {
            maxLength: this.maxLength
          })
        }
      }
    },
    requiredFieldRule () {
      return (v) => {
        return (v && v.trim().length > 0) || this.$t('message.pages.organization.directCancel.inputDescription.requiredField')
      }
    }
  },
  mounted() {
    this.$root.$on('cancelSubscriptionDirectModal', (token) => {
      this.resetState()
      this.hideCrispButton()
      this.open = true
      this.unsubscribeToken = token
    })
  },
  beforeDestroy() {
    this.$root.$off('cancelSubscriptionDirectModal')
    this.$root.$emit('resetIsAnyOtherModal')
    this.$root.$emit('cancelSubscriptionDirectModalHasClosed')
    this.showCrispButton()
  },
  methods: {
    ...mapMutations('user', [
      'UPDATE_GLOBAL_SNACKBAR',
      'UPDATE_USER_PLAN_INFO'
    ]),
    ...mapActions('session', ['signInUser']),
    async handleCancellation() {
      this.isFetching = true
      this.apiError = null
      try {
        await this.signInUser({
          username: this.$store.getters['session/userAttributes'].email,
          password: this.cancelData.password
        })

        this.errors.passwordError = false

        await planApi.cancelSubscription(this.cancelData.reason, this.unsubscribeToken),
        
        this.UPDATE_USER_PLAN_INFO({
          ...this.userPlanInfo,
          subscription_id: null,
          subscription: null
        })

        const url = new URL(window.location.href)
        if (url.searchParams.has('unsubscribe_token')) {
          url.searchParams.delete('unsubscribe_token')
          window.history.replaceState({}, '', url.toString())
        }

        const successMessage = i18n.t('message.pages.organization.directCancel.cancelSuccess')
        this.UPDATE_GLOBAL_SNACKBAR({
          open: true,
          type: 'info',
          text: successMessage
        })
        
        this.handleClose()
      } catch (error) {
        if (
          (error.message && error.message.includes('Incorrect username or password')) ||
          error.name === 'NotAuthorizedException' ||
          error.message === 'NotAuthorizedException: Incorrect username or password.'
        ) {
          this.errors.passwordError = true
          this.apiError = null
          return
        }

        const errorMap = {
          'reason is required': 'missingReason',
          [`reason must be at most ${this.maxLength} characters`]: 'reasonTooLong',
          'customer not found': 'customerNotFound',
          'customer has no subscriptions': 'noSubscription',
          'must have required property \'unsubscribe_token\' in body': 'tokenRequired',
          'unsubscribe token not found': 'tokenNotFound',
          'error validating unsubscribe token': 'tokenValidationError'
        }

        let errorMessage = error?.response?.data?.errMsg
        if (typeof errorMessage === 'object' && errorMessage?.error) {
          errorMessage = errorMessage.error
        }
        const errorKey = errorMap[errorMessage] || 'cancelFailed'
        this.apiError = this.$t(`message.pages.organization.directCancel.errors.${errorKey}`)
        this.errors.passwordError = false
      } finally {
        this.isFetching = false
      }
    },
    handleClose() {
      this.open = false
      this.showCrispButton()
      this.$root.$emit('cancelSubscriptionDirectModalHasClosed')
    },
    inputBeingEdited(i, ref) {
      this.activeInput = i
      if (ref) {
        this.activeInput = i + ref
      }
    },
    cancelEdit() {
      this.activeInput = null
    },
    resetState() {
      this.cancelData = {
        reason: '',
        password: '',
        confirmAction: false
      }
      
      this.errors = {
        exceededMaxLength: false,
        passwordError: false
      }
      
      this.activeInput = null
      this.isFetching = false

      this.$nextTick(() => {
        this.$refs.reason?.$refs?.baseTextarea?.resetValidation?.()
      })
    },
    hideCrispButton() {
      const crispButton = document.querySelector('.crisp-client')
      if (crispButton && this.isMobileBreakpoint) {
        crispButton.style.setProperty('display', 'none', 'important')
      }
    },
    showCrispButton() {
      const crispButton = document.querySelector('.crisp-client')
      if (crispButton) {
        crispButton.style.removeProperty('display')
      }
    },
  },
  watch: {
    '$i18next.resolvedLanguage' () {
      this.$nextTick(() => {
        this.$refs.reason?.$refs?.baseTextarea?.validate?.()
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.instructions {
  color: varFigma(neutral-30-base);
}

.toolbar-title {
  color: varFigma(neutral-20-dark);
  font-size: 18px;
  font-style: normal;
  font-weight: 500;
  line-height: 120%;
}

.text-caption {
  color: varFigma(neutral-40-light);
  font-size: 12px;
  line-height: 150%;
  margin-top: 4px;
  margin-bottom: 0;
}

.base-alert {
  align-items: flex-start;
}

.mobile-actions {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  background-color: var(--v-background-base);
  z-index: 10;
  padding: 16px;
}

.container-title-description::v-deep {
  & .v-input__control .v-input__slot {
    padding: 0 !important;
    border: 1px solid varFigma(neutral-95-lightest);

    & .v-text-field__slot {
      padding: 0 12px !important;
      margin-right: 0 !important;
      border-radius: var(--spacing-02, 4px);
    }
  }
  & .v-input__control .v-text-field__details {
    margin-bottom: 0 !important;
    padding-left: 0 !important;
    .v-counter {
      color: varFigma(neutral-40-light) !important;
      text-align: right;
      font-size: 12px;
      font-style: normal;
      font-weight: 400;
      line-height: 150%;
    }
  }
}

.description-input::v-deep {
  & .v-text-field__slot {
    textarea {
      margin-top: 0;
      height: 128px !important;
      overflow-y: auto;
    }

    textarea::placeholder {
      font-size: 14px !important;
    }
  }
}

.password-input::v-deep {
  label {
    left: -10px !important;
  }

  input::placeholder {
    font-size: 14px !important;
  }
}

.v-toolbar__title {
  white-space: normal;
}

::v-deep .v-toolbar__content {
  align-items: baseline;
  height: auto !important;

  .v-toolbar__items {
    height: unset;

    button {
      border-radius: 50%;
      width: 31.2px !important;
      height: 31.2px !important;
    }
  }
}

.api-error-message {
  color: var(--v-error-base);
  font-size: 12px;
  margin-top: 4px;
  display: block;
}

@media (max-width: 375px) {
  .mobile-actions {
    display: flex;
    flex-direction: column;
    padding: 16px !important;

    .button-container {
      width: 100%;
    }

    button {
      width: 100% !important;
      margin: 0 !important;

      &:last-child {
        margin-top: 12px !important;
      }
    }
  }
}
</style>